import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, CircularProgress, Alert, Stack,
  Autocomplete, useMediaQuery, useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { exportService } from '../../services/accounting/export.service';
import { serverMessage } from '../../utils/apiConvert';
import { toYmd } from '../../utils/accounting/periodUtils';

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const METHOD_LABELS = {
  BANK_TRANSFER: 'Bank Transfer', CASH: 'Cash Deposit', WALLET: 'E-Wallet', ONLINE: 'Online Payment', CHEQUE: 'Cheque',
};
const INITIATOR_LABELS = { TC: 'Typing center', CUSTOMER: 'Customer' };

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

// Bar chart colors
const BAR_COLORS = ['#2e7d32', '#1565c0', '#e65100', '#6a1b9a', '#c62828', '#00695c'];

const CollectionReport = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creditPayments, setCreditPayments] = useState([]);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [detailRows, setDetailRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [receivedTotal, setReceivedTotal] = useState(0);
  const [settledTotal, setSettledTotal] = useState(0);

  // Filters (default: today)
  const [dateFrom, setDateFrom] = useState(toYmd(new Date()));
  const [dateTo, setDateTo] = useState(toYmd(new Date()));
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    financeReportsService.getCustomers(user.typingCenterId || user.typing_center_id).then(setCustomers).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeReportsService.getCollections({ from: dateFrom, to: dateTo, customerId: selectedCustomer?.id });
      // Money received is the collection; wallet settlements only apply money already received (shown, not added)
      setCreditPayments((res?.receivedByMethod || []).map((p) => ({ id: p.method, method: METHOD_LABELS[p.method] || p.method, count: p.count, amount: Number(p.amount) })));
      setInvoicePayments((res?.settledByInitiator || []).map((p) => ({ id: p.method, method: INITIATOR_LABELS[p.method] || p.method, count: p.count, amount: Number(p.amount) })));
      setReceivedTotal(Number(res?.receivedTotal || 0));
      setSettledTotal(Number(res?.settledTotal || 0));
      setDetailRows([
        ...(res?.received || []).map((r, i) => ({ id: `r-${i}`, date: r.date, customer: r.customer, type: 'CREDIT', method: METHOD_LABELS[r.method] || r.method, reference: r.reference, amount: Number(r.amount) })),
        ...(res?.settled || []).map((r, i) => ({
          id: `s-${i}`, date: r.date, customer: r.customer, type: 'INVOICE',
          method: `Wallet · by ${(INITIATOR_LABELS[r.initiatedBy] || r.initiatedBy || '').toLowerCase()}`,
          reference: `${r.receiptNumber} → ${r.invoiceNumber}`, amount: Number(r.amount),
        })),
      ].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)));
    } catch (err) {
      setCreditPayments([]);
      setInvoicePayments([]);
      setDetailRows([]);
      setReceivedTotal(0);
      setSettledTotal(0);
      setError(serverMessage(err, 'Failed to load collection report'));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedCustomer]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Export with the page's own filters (BE-14b); the file comes back under the server's name
  const [exporting, setExporting] = useState(null);
  const doExport = async (format) => {
    setExporting(format);
    try {
      await exportService.download('collections', format, { from: dateFrom, to: dateTo, customerId: selectedCustomer?.id });
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(null);
    }
  };

  const paginatedDetails = detailRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const totalCreditCollection = receivedTotal;
  const totalInvoiceCollection = settledTotal;
  const totalCollection = receivedTotal;

  // Money received by method for the bar chart
  const allMethods = {};
  creditPayments.forEach(p => {
    allMethods[p.method] = (allMethods[p.method] || 0) + p.amount;
  });
  const chartData = Object.entries(allMethods).sort((a, b) => b[1] - a[1]);
  const maxAmount = chartData.length > 0 ? chartData[0][1] : 1;

  // ── Bar Chart (simple CSS bars) ──
  const renderBarChart = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 2, color: 'secondary.main' }}>
        Collection by Payment Method
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {chartData.map(([method, amount], idx) => (
          <Box key={method}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{method}</Typography>
              <Typography sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>{formatCurrency(amount)}</Typography>
            </Box>
            <Box sx={{ width: '100%', height: 20, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f5f5f5', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{
                width: `${(amount / maxAmount) * 100}%`,
                height: '100%',
                bgcolor: isDark
                  ? ['#4caf50', '#42a5f5', '#ff9800', '#ab47bc', '#ef5350', '#26a69a'][idx % 6]
                  : BAR_COLORS[idx % BAR_COLORS.length],
                borderRadius: 1,
                transition: 'width 0.5s ease'
              }} />
            </Box>
          </Box>
        ))}
      </Box>
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700 }}>
          Total: {formatCurrency(totalCollection)}
        </Typography>
      </Box>
    </Paper>
  );

  // ── Summary Section (Credit + Invoice) ──
  const renderSummarySection = (title, payments, total, color, firstColumn = 'Payment Method', note = null) => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: note ? 0.25 : 1.5, color: 'secondary.main' }}>
        {title}
      </Typography>
      {note && <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1.5 }}>{note}</Typography>}
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>{firstColumn}</TableCell>
            <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Count</TableCell>
            <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Total Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payments.length === 0 && (
            <TableRow><TableCell colSpan={3} sx={{ fontSize: '13px', color: 'text.secondary', textAlign: 'center' }}>None in this period</TableCell></TableRow>
          )}
          {payments.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell sx={{ fontSize: '13px', py: 1 }}>{p.method}</TableCell>
              <TableCell align="center" sx={{ fontSize: '13px', py: 1 }}>{p.count}</TableCell>
              <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 600, color }}>
                {formatCurrency(p.amount)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9' }}>
            <TableCell sx={{ fontSize: '13px', fontWeight: 700, py: 0.75 }}>Total</TableCell>
            <TableCell align="center" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75 }}>
              {payments.reduce((sum, p) => sum + p.count, 0)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75, color }}>
              {formatCurrency(total)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography sx={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 600, color: 'secondary.main' }}>
            Collection Report
          </Typography>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
            Money received grouped by method; wallet settlements shown alongside
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" variant="contained" startIcon={<ExcelIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('csv')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </Button>
          <Button size="small" variant="contained" startIcon={<PdfIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('pdf')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </Button>
          <Button size="small" variant="outlined" onClick={fetchData}
            sx={{ minWidth: 36 }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="From Date" type="date"
              value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="To Date" type="date"
              value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete size="small" options={customers}
              value={selectedCustomer}
              onChange={(e, v) => { setSelectedCustomer(v); setPage(0); }}
              renderInput={(params) => <TextField {...params} label="Customer / Organization" />}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <>
          {/* Bar Chart */}
          {renderBarChart()}

          {/* Two-column summary on desktop, stacked on mobile */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderSummarySection('Money Received (Wallet Top-ups)', creditPayments, totalCreditCollection, '#4caf50')}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSummarySection('Settled from Wallet', invoicePayments, totalInvoiceCollection, '#1565c0', 'Initiated By',
                'Wallet money applied to invoices — already counted when it was received, so not added to the total')}
            </Grid>
          </Grid>

          {/* Grand Total */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: isDark ? 'rgba(76,175,80,0.10)' : '#e8f5e9' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: fontSize.md, fontWeight: 700 }}>Total Collection</Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: isDark ? '#4caf50' : '#2e7d32' }}>
                {formatCurrency(totalCollection)}
              </Typography>
            </Box>
          </Paper>

          {/* Detail Table */}
          <Paper sx={{ mb: 0 }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>
                Transaction Details
              </Typography>
            </Box>
            {isMobile ? (
              <Stack spacing={1.5} sx={{ px: 2, pb: 2 }}>
                {paginatedDetails.length === 0 ? (
                  <Typography color="text.secondary" sx={{ fontSize: fontSize.sm, textAlign: 'center', py: 2 }}>No transactions found</Typography>
                ) : paginatedDetails.map((row) => (
                  <Card key={row.id} variant="outlined">
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={0.5}>
                        <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>{row.customer}</Typography>
                        <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: row.type === 'CREDIT' ? '#4caf50' : '#1565c0' }}>
                          {formatCurrency(row.amount)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                        {formatDate(row.date)} - {row.method} - {row.reference}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                      <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Customer</TableCell>
                      <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Type</TableCell>
                      <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Method</TableCell>
                      <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Reference</TableCell>
                      <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDetails.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No transactions found</Typography></TableCell></TableRow>
                    ) : (
                      <>
                        {paginatedDetails.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell sx={{ fontSize: '13px', py: 1, color: 'text.secondary' }}>{formatDate(row.date)}</TableCell>
                            <TableCell sx={{ fontSize: '13px', py: 1 }}>{row.customer}</TableCell>
                            <TableCell sx={{ fontSize: '13px', py: 1 }}>
                              <Typography sx={{
                                fontSize: '13px', fontWeight: 500,
                                color: row.type === 'CREDIT' ? '#4caf50' : '#1565c0'
                              }}>
                                {row.type === 'CREDIT' ? 'Wallet Top-up' : 'Wallet Settlement'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', py: 1 }}>{row.method}</TableCell>
                            <TableCell sx={{ fontSize: '13px', py: 1, fontFamily: 'monospace', color: 'text.secondary' }}>{row.reference}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 600, color: row.type === 'CREDIT' ? '#4caf50' : '#1565c0' }}>
                              {formatCurrency(row.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Summary Row */}
                        <TableRow sx={{ bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9' }}>
                          <TableCell colSpan={5} sx={{ fontSize: '13px', fontWeight: 700, py: 1.5 }}>
                            TOTAL RECEIVED ({detailRows.filter(r => r.type === 'CREDIT').length} transactions; settlements not added)
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75 }}>
                            {formatCurrency(totalCollection)}
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination component="div" count={detailRows.length} page={page}
              onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default CollectionReport;
